import { useRef, useState } from "react";
import axios from "axios";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Upload, Trash2, Image as ImageIcon, ShieldAlert, Shuffle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABELS = {
  logo: { title: "Logo", hint: "PNG/SVG/WebP, max 500 KB. Wird in der Top-Nav statt dem Firmennamen angezeigt.", ratio: "w-[88px] h-11", empty: "Noch kein Logo" },
  hero: { title: "Hero-Bild", hint: "Großes Titelbild (ideal 1920×1080). Max 3 MB. Ersetzt das KI-generierte Bild.", ratio: "w-24 h-14", empty: "Aktuell: KI-generiertes Bild" },
  about: { title: "About-Bild", hint: "Bild in der Über-uns-Sektion. Max 3 MB.", ratio: "w-24 h-14", empty: "Aktuell: Unsplash-Stockfoto" },
};

/**
 * Props:
 *   projectId: string
 *   slot: "logo" | "hero" | "about"
 *   currentUrl: string
 *   onUploaded: (url: string) => void
 *   onDeleted: () => void
 *   rerollSlot?: string   // if set → enable reroll ("about" | "services_0"...)
 */
export const MediaUploader = ({ projectId, slot, currentUrl, onUploaded, onDeleted, rerollSlot }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [rerolling, setRerolling] = useState(false);
  const conf = LABELS[slot] || LABELS.logo;

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const url = slot === "logo"
        ? `${API}/projects/${projectId}/upload-logo`
        : `${API}/projects/${projectId}/upload-image?slot=${slot}`;
      const res = await axios.post(url, form, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      const dataUrl = res.data.logo_url || res.data.url;
      onUploaded(dataUrl);
      toast.success(`${conf.title} hochgeladen`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/projects/${projectId}/media/${slot}`, { withCredentials: true });
      onDeleted();
      toast.success(`${conf.title} entfernt`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const handleReroll = async () => {
    if (!rerollSlot) return;
    setRerolling(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/reroll-image?slot=${rerollSlot}`, null, { withCredentials: true });
      onUploaded(res.data.url);
      toast.success("Neues Bild gewählt");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Würfeln fehlgeschlagen");
    } finally {
      setRerolling(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={`media-uploader-${slot}`}>
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">{conf.title}</Label>
        <div className="flex items-center gap-2">
          {rerollSlot && (
            <button type="button" onClick={handleReroll} disabled={rerolling} className="text-primary hover:text-primary/80 transition-colors text-xs flex items-center gap-1 disabled:opacity-50" data-testid={`reroll-${slot}-btn`}>
              <Shuffle size={11} className={rerolling ? "animate-spin" : ""} /> {rerolling ? "würfelt..." : "Bild würfeln"}
            </button>
          )}
          {currentUrl && (
            <button type="button" onClick={handleDelete} className="text-muted-foreground hover:text-destructive transition-colors text-xs flex items-center gap-1" data-testid={`remove-${slot}-btn`}>
              <Trash2 size={11} /> Entfernen
            </button>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className={`${conf.ratio} rounded-sm border border-border bg-muted/30 overflow-hidden flex items-center justify-center shrink-0`}>
          {currentUrl ? (
            <img src={currentUrl} alt={conf.title} className="max-w-full max-h-full object-contain" />
          ) : (
            <ImageIcon size={16} className="text-muted-foreground" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <Button type="button" size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading} className="rounded-sm text-xs h-7 px-2.5 gap-1.5" data-testid={`upload-${slot}-btn`}>
            <Upload size={11} /> {uploading ? "Lädt hoch..." : (currentUrl ? "Ersetzen" : "Hochladen")}
          </Button>
          <p className="text-[11px] text-muted-foreground mt-1.5 leading-tight">{currentUrl ? conf.hint : conf.empty}</p>
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
        data-testid={`file-input-${slot}`}
      />
      {slot === "hero" && (
        <p className="text-[11px] text-muted-foreground italic flex items-start gap-1">
          <ShieldAlert size={10} className="mt-0.5 shrink-0" />
          Änderung wird erst nach „Speichern & Vorschau aktualisieren" sichtbar.
        </p>
      )}
    </div>
  );
};
